import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import validate from 'validate.js';
import moment from 'moment';
import { Alert, Grid, Stack, Typography } from '@mui/material';
import {
  DatePickerV2,
  DialogDefaultV2,
  InputV2,
  SimNaoSelectV2,
} from '../../../components/v2';
import BaseService from '../../../services/BaseService';
import { RELATORIO_JOB_POLL_MS, RELATORIO_JOB_TIPO } from '../../../helpers/relatorioJob';

const schema = {
  incluirFotos: { presence: { allowEmpty: false, message: 'é obrigatório' } },
  local: { length: { maximum: 200, message: 'deve ter no máximo 200 caracteres' } },
};

const emptyValues = {
  incluirFotos: '',
  local: '',
  dataExecucao: null,
};

const apiMessage = (error, fallback) => {
  const data = error && error.response && error.response.data;
  if (!data) return fallback;
  if (typeof data === 'string' && data.trim()) return data;
  if (typeof data.mensagem === 'string' && data.mensagem) return data.mensagem;
  if (typeof data.message === 'string' && data.message) return data.message;
  return fallback;
};

const firstError = (errors, key) => {
  if (!errors || !errors[key]) return '';
  const value = errors[key];
  return Array.isArray(value) ? value[0] : String(value);
};

const fotoAusente = (opcoes) => {
  if (!opcoes) return '';
  const partes = [];
  if (!opcoes.possuiFotoInicial) partes.push('inicial');
  if (!opcoes.possuiFotoFinal) partes.push('final');
  if (!partes.length) return '';
  return `Não há foto ${partes.join(' e ')} anexada. Marcar Sim não inclui esse arquivo.`;
};

const CcsmDialog = ({
  open,
  nsirId,
  onClose,
  onExcel,
  downloadRelatorioJob,
}) => {
  const [step, setStep] = useState('escolha');
  const [opcoes, setOpcoes] = useState(null);
  const [values, setValues] = useState(emptyValues);
  const [fieldErrors, setFieldErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState('');
  const [avisos, setAvisos] = useState([]);
  const [jobId, setJobId] = useState(null);

  useEffect(() => {
    if (!open) return;
    setStep('escolha');
    setOpcoes(null);
    setValues(emptyValues);
    setFieldErrors({});
    setErrorMessage('');
    setAvisos([]);
    setJobId(null);
  }, [open]);

  useEffect(() => {
    if (!open || step !== 'gerando' || !jobId) return undefined;
    let cancelled = false;
    let timer;

    const tick = async () => {
      try {
        const response = await BaseService.get({ url: `reports/ccsm/${jobId}` });
        if (cancelled) return;
        const data = response.data || {};
        if (data.status === 'AVAILABLE') {
          setAvisos(Array.isArray(data.avisos) ? data.avisos.filter(Boolean) : []);
          setStep('pronto');
          return;
        }
        if (data.status === 'ERROR') {
          setErrorMessage(data.error || 'Falha ao gerar o PDF.');
          setStep('erro');
          return;
        }
        timer = window.setTimeout(tick, RELATORIO_JOB_POLL_MS);
      } catch (error) {
        if (cancelled) return;
        setErrorMessage(apiMessage(error, 'Erro ao consultar a geração do PDF.'));
        setStep('erro');
      }
    };

    tick();
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [open, step, jobId]);

  const applyJob = (data) => {
    if (!data || !data.id) {
      setErrorMessage('O servidor não devolveu o job do PDF.');
      setStep('erro');
      return;
    }
    setJobId(data.id);
    if (data.status === 'AVAILABLE') {
      setAvisos(Array.isArray(data.avisos) ? data.avisos.filter(Boolean) : []);
      setStep('pronto');
      return;
    }
    if (data.status === 'ERROR') {
      setErrorMessage(data.error || 'Falha ao gerar o PDF.');
      setStep('erro');
      return;
    }
    setStep('gerando');
  };

  const loadOpcoes = async () => {
    setErrorMessage('');
    setStep('carregando');
    try {
      const response = await BaseService.get({ url: `reports/ccsm/${nsirId}/opcoes` });
      const data = response.data || {};
      setOpcoes(data);
      setValues({
        incluirFotos: data.sugestaoIncluirFotos ? 'SIM' : 'NAO',
        local: data.local || '',
        dataExecucao: data.dataExecucao || null,
      });
      setFieldErrors({});
      setStep('formulario');
    } catch (error) {
      setErrorMessage(apiMessage(error, 'Não foi possível carregar as opções do CCSM.'));
      setStep('escolha');
    }
  };

  const gerarPdf = async () => {
    const validation = validate(values, schema) || {};
    const nextErrors = { ...validation };
    if (values.dataExecucao && moment(values.dataExecucao).isAfter(moment(), 'day')) {
      nextErrors.dataExecucao = ['A data de execução não pode ser futura.'];
    }
    setFieldErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const body = {
      incluirFotos: values.incluirFotos === 'SIM',
    };
    const local = (values.local || '').trim();
    if (local && local !== (opcoes && opcoes.local ? opcoes.local : '')) {
      body.local = local;
    }
    const dataExecucao = values.dataExecucao
      ? moment(values.dataExecucao).format('YYYY-MM-DD')
      : null;
    if (dataExecucao && dataExecucao !== (opcoes && opcoes.dataExecucao)) {
      body.dataExecucao = dataExecucao;
    }

    setErrorMessage('');
    setJobId(null);
    setStep('gerando');
    try {
      const response = await BaseService.post({
        url: `reports/ccsm/${nsirId}`,
        data: body,
      });
      applyJob(response.data);
    } catch (error) {
      setErrorMessage(apiMessage(error, 'Não foi possível gerar o PDF.'));
      setStep('formulario');
    }
  };

  const baixarPdf = () => {
    if (!jobId) return;
    downloadRelatorioJob({
      id: jobId,
      tipo: RELATORIO_JOB_TIPO.CCSM,
    });
  };

  const onConfirm = () => {
    if (step === 'escolha') {
      loadOpcoes();
      return;
    }
    if (step === 'formulario') {
      gerarPdf();
      return;
    }
    if (step === 'pronto') {
      baixarPdf();
      return;
    }
    if (step === 'erro') {
      setErrorMessage('');
      setStep(opcoes ? 'formulario' : 'escolha');
    }
  };

  const fotoAviso = fotoAusente(opcoes);
  const confirmLoading = step === 'carregando';
  let confirmMessage = 'Gerar PDF';
  if (step === 'gerando') confirmMessage = 'Gerando PDF…';
  if (step === 'pronto') confirmMessage = 'Baixar PDF';
  if (step === 'erro') confirmMessage = 'Voltar';

  return (
    <DialogDefaultV2
      open={open}
      handlerCancel={onClose}
      handlerConfirm={onConfirm}
      title="CCSM"
      cancelMessage="Fechar"
      confirmMessage={confirmMessage}
      confirmLoading={confirmLoading}
      confirmLoadingMessage="Carregando…"
      disabledConfirmButton={confirmLoading || step === 'gerando'}
      maxWidth="sm"
      fullWidth
      extraActions={step === 'escolha' ? [{
        label: 'Baixar Excel',
        variant: 'outlined',
        onClick: onExcel,
      }] : undefined}
    >
      <Stack spacing={2}>
        {step === 'escolha' && (
          <Typography variant="body2">
            O Excel é o relatório atual, só com a página 1. O PDF é o CCSM completo, para conferência.
          </Typography>
        )}
        {errorMessage && (
          <Alert severity="error">{errorMessage}</Alert>
        )}
        {step === 'formulario' && opcoes && (
          <>
            {opcoes.mensagem && (
              <Alert severity="info">{opcoes.mensagem}</Alert>
            )}
            {opcoes.dataExecucaoAproximada && (
              <Alert severity="warning">
                A data sugerida veio da finalização da inspeção e pode estar um dia depois da execução real.
              </Alert>
            )}
            {fotoAviso && (
              <Alert severity="warning">{fotoAviso}</Alert>
            )}
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <SimNaoSelectV2
                  isRequired
                  label="Adicionar fotos inicial e final"
                  name="incluirFotos"
                  error={!!fieldErrors.incluirFotos}
                  value={values.incluirFotos}
                  onChange={(event) => {
                    setValues((prev) => ({ ...prev, incluirFotos: event.target.value }));
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 7 }}>
                <InputV2
                  label="Local"
                  name="local"
                  value={values.local}
                  error={!!fieldErrors.local}
                  helperText={firstError(fieldErrors, 'local')}
                  onChange={(event) => {
                    setValues((prev) => ({ ...prev, local: event.target.value }));
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 5 }}>
                <DatePickerV2
                  label="Data de execução"
                  value={values.dataExecucao}
                  error={!!fieldErrors.dataExecucao}
                  onChange={(value) => {
                    setValues((prev) => ({ ...prev, dataExecucao: value }));
                  }}
                />
                {fieldErrors.dataExecucao && (
                  <Typography variant="caption" color="error">
                    {firstError(fieldErrors, 'dataExecucao')}
                  </Typography>
                )}
              </Grid>
            </Grid>
          </>
        )}
        {step === 'gerando' && (
          <Typography variant="body2">
            O PDF está sendo montado. Pode fechar esta janela: a notificação avisa quando o arquivo ficar pronto.
          </Typography>
        )}
        {step === 'pronto' && (
          <>
            <Alert severity="success">PDF pronto.</Alert>
            {avisos.map((aviso) => (
              <Alert key={aviso} severity="warning">{aviso}</Alert>
            ))}
          </>
        )}
      </Stack>
    </DialogDefaultV2>
  );
};

CcsmDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  nsirId: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onExcel: PropTypes.func.isRequired,
  downloadRelatorioJob: PropTypes.func.isRequired,
};

CcsmDialog.defaultProps = {
  nsirId: null,
};

export default CcsmDialog;
